import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import { isRemovedValidationFieldName } from '../validationConstants';
import type { PersistedParameterReference } from '../model/pcfParameterMappingModel';

export type HostPcfMappingEntry = {
  Key: string;
  RevitParameterName: string;
  ParameterReference?: PersistedParameterReference | null;
  IsEnabled: boolean;
  Notes: string;
  LastModified: string;
};

export type HostPcfMappingConfig = {
  Mappings: HostPcfMappingEntry[];
  ConfigName: string;
  LastModified: string;
  Version: number;
};

export const PCF_KEY_ALIASES: Record<string, string> = {
  OBJECTTYPE: 'ObjectType',
  PCFOBJECTTYPE: 'ObjectType',
  SKEY: 'Skey',
  PCFSKEY: 'Skey',
  ENDTYPE1: 'EndType1',
  ENDTYPE2: 'EndType2',
  ENDTYPE3: 'EndType3',
  PIPINGSPEC: 'PipingSpec',
  ENDPOINT1: 'EndPoint1',
  ENDPOINT2: 'EndPoint2',
  ENDPOINT3: 'EndType3',
  MATERIALDESCRIPTION: 'MaterialDescription',
  MATERIAL: 'MaterialDescription',
  PRESSURECLASS: 'PressureClass',
  DESIGNSTANDARD: 'DesignStandard',
  DESIGNSTD: 'DesignStandard',
  INSULATIONSPEC: 'InsulationSpec',
  TRACINGSPEC: 'TracingSpec',
  INSULATIONTHICKNESS: 'InsulationThickness',
  WEIGHT: 'Weight',
  ITEMDESCRIPTION: 'ItemDescription',
  BOLTLENGTH: 'BoltLength',
  BOLTDIAMETER: 'BoltDiameter',
  BOLTDIA: 'BoltDiameter',
  BOLTDIABOLTDIAMETER: 'BoltDiameter',
  BOLTSETACTIVE: 'BoltSetActive',
  PIPESCHEDULE: 'PipeSchedule',
  COMPONENTIDENTIFIER1: 'ComponentIdentifier1',
  COMPONENTIDENTIFIER2: 'ComponentIdentifier2',
};

export function normalizePcfToken(value: string): string {
  return value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function resolvePcfMappingKey(rawName: string): string | null {
  const normalized = normalizePcfToken(rawName);
  return PCF_KEY_ALIASES[normalized] ?? null;
}

export function setOverride(overrides: Record<string, string>, key: string, value: string | undefined) {
  const trimmed = (value || '').trim();
  if (trimmed) {
    overrides[key] = trimmed;
  }
}

export function buildValidationMappingOverridesFromStore(): Record<string, string> {
  const state = useConfigurationStore.getState();
  const overrides: Record<string, string> = {};
  const mirrorEndpointMapping = (pcfKey: string, mappedParam: string) => {
    if (pcfKey === 'EndPoint1') {
      setOverride(overrides, 'EndType1', mappedParam);
    } else if (pcfKey === 'EndPoint2') {
      setOverride(overrides, 'EndType2', mappedParam);
    } else if (pcfKey === 'EndType3') {
      setOverride(overrides, 'EndType3', mappedParam);
    }
  };

  const objectTypes = state.mappingConfiguration.general.mappingTable.objectType || [];
  const allObjectType = objectTypes.find(
    (objectType) => (objectType.name || '').trim().toUpperCase() === 'ALL'
  );
  const mappingSources = allObjectType
    ? [allObjectType, ...objectTypes.filter((objectType) => objectType !== allObjectType)]
    : objectTypes;

  for (const objectType of mappingSources) {
    const attributes = objectType.attributes || [];
    for (const attribute of attributes) {
      const key = resolvePcfMappingKey(attribute.pcfAttributeName || '');
      if (!key) {
        continue;
      }

      if ((attribute.attributeType || '').toLowerCase() === 'formula') {
        continue;
      }

      const mappedParam = (attribute.revitParam || '').trim();
      if (mappedParam && mappedParam.toLowerCase() !== 'formula') {
        if (!overrides[key]) {
          overrides[key] = mappedParam;
        }
        mirrorEndpointMapping(key, mappedParam);
      }
    }
  }

  setOverride(overrides, 'ObjectType', state.menuConfiguration.objectTypeValue);
  setOverride(overrides, 'Skey', state.menuConfiguration.sKeyValue);
  setOverride(overrides, 'PipingSpec', state.menuConfiguration.pipingSpecValue || state.processPropertiesConfiguration.pipingSpecValuePC?.value);
  setOverride(overrides, 'EndPoint1', state.connectorBindingConfiguration.endpoint1Value.value);
  setOverride(overrides, 'EndPoint2', state.connectorBindingConfiguration.endpoint2Value.value);
  setOverride(overrides, 'EndType1', state.connectorBindingConfiguration.endpoint1Value.value);
  setOverride(overrides, 'EndType2', state.connectorBindingConfiguration.endpoint2Value.value);
  setOverride(overrides, 'EndType3', state.connectorBindingConfiguration.endpoint3Value.value);
  setOverride(overrides, 'MaterialDescription', state.connectorBindingConfiguration.materialValue.value);
  setOverride(overrides, 'PressureClass', state.connectorBindingConfiguration.pressureClassValue.value);
  setOverride(overrides, 'DesignStandard', state.connectorBindingConfiguration.designStdValue.value);
  setOverride(overrides, 'InsulationSpec', state.connectorBindingConfiguration.insulationSpecValue.value);
  setOverride(overrides, 'InsulationThickness', state.connectorBindingConfiguration.insulationThicknessValue.value);
  setOverride(overrides, 'TracingSpec', state.connectorBindingConfiguration.tracingSpecValue.value);
  setOverride(overrides, 'ItemDescription', state.connectorBindingConfiguration.itemDescriptionValue.value);
  setOverride(overrides, 'BoltLength', state.boltConfiguration.boltLengthValue.value);
  setOverride(overrides, 'BoltDiameter', state.boltConfiguration.boltDiaValue.value);
  setOverride(overrides, 'BoltSetActive', state.boltConfiguration.boltSetActiveValue.value);

  return overrides;
}

export function toHostConfigCandidate(raw: any): HostPcfMappingConfig | null {
  const serializedCandidate =
    typeof raw?.configJson === 'string'
      ? raw.configJson
      : typeof raw?.ConfigJson === 'string'
        ? raw.ConfigJson
        : typeof raw?.value?.configJson === 'string'
          ? raw.value.configJson
          : null;
  let parsedCandidate: HostPcfMappingConfig | null = null;
  if (serializedCandidate) {
    try {
      parsedCandidate = JSON.parse(serializedCandidate) as HostPcfMappingConfig;
    } catch {
      parsedCandidate = null;
    }
  }
  const candidate = parsedCandidate ?? raw?.Value ?? raw;
  if (!candidate || typeof candidate !== 'object' || !Array.isArray(candidate.Mappings)) {
    return null;
  }

  return candidate as HostPcfMappingConfig;
}

export function mergeMappingConfigWithOverrides(
  currentConfig: HostPcfMappingConfig | null,
  overrides: Record<string, string>
): HostPcfMappingConfig {
  const nowIso = new Date().toISOString();
  const existingMappings = Array.isArray(currentConfig?.Mappings)
    ? currentConfig!.Mappings.map((entry) => ({
        Key: String(entry.Key || ''),
        RevitParameterName: String(entry.RevitParameterName || ''),
        ParameterReference: entry.ParameterReference ?? null,
        IsEnabled: entry.IsEnabled !== false,
        Notes: String(entry.Notes || ''),
        LastModified: String(entry.LastModified || nowIso),
      })).filter((entry) => !isRemovedValidationFieldName(entry.Key))
    : [];

  const mappingByKey = new Map<string, HostPcfMappingEntry>();
  for (const mapping of existingMappings) {
    if (mapping.Key) {
      mappingByKey.set(mapping.Key, mapping);
    }
  }

  for (const [key, value] of Object.entries(overrides)) {
    const existing = mappingByKey.get(key);
    if (existing) {
      if (existing.RevitParameterName !== value) {
        existing.ParameterReference = null;
      }
      existing.RevitParameterName = value;
      existing.IsEnabled = true;
      existing.LastModified = nowIso;
    } else {
      mappingByKey.set(key, {
        Key: key,
        RevitParameterName: value,
        ParameterReference: null,
        IsEnabled: true,
        Notes: '',
        LastModified: nowIso,
      });
    }
  }

  return {
    ConfigName: currentConfig?.ConfigName || 'PipeEX Validation Runtime Mapping',
    Version: currentConfig?.Version || 1,
    LastModified: nowIso,
    Mappings: Array.from(mappingByKey.values()),
  };
}

export function mappingConfigsHaveSameEnabledBindings(
  left: HostPcfMappingConfig | null,
  right: HostPcfMappingConfig
): boolean {
  if (!left?.Mappings) {
    return false;
  }

  const leftEntries = left.Mappings
    .map((mapping): [string, { parameterName: string; isEnabled: boolean }] => [
      String(mapping.Key || '').trim(),
      {
        parameterName: String(mapping.RevitParameterName || '').trim(),
        isEnabled: mapping.IsEnabled !== false,
      },
    ])
    .filter(([key]) => Boolean(key));

  const leftByKey = new Map(leftEntries);

  return right.Mappings.every((mapping) => {
    const key = String(mapping.Key || '').trim();
    const existing = leftByKey.get(key);
    return Boolean(existing)
      && existing!.isEnabled === (mapping.IsEnabled !== false)
      && existing!.parameterName === String(mapping.RevitParameterName || '').trim();
  });
}
